import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import {
  KeyRound, LayoutGrid, List, Mail, MapPin, Pencil, ShieldCheck, Trash2, UserPlus,
  Users, X,
} from 'lucide-react';

import { useXomicsAuth } from './AuthContext';
import {
  deleteStaff, inviteStaff, loadAssignableRoles, loadLocations, loadStaff,
  resendStaffInvitation, updateStaff,
} from './api';
import {
  Empty, Failed, Head, Loading, Pill, SearchField, Who, WorkspaceTabs, XomicsSelect,
} from './parts';
import { Field, errorMessage, fieldAria } from './ui';
import { useLiveResource } from './useLive';
import { tx, useXomicsI18n } from './i18n';
import { codeLabel, roleLabel } from './labels';

function when(value) {
  return value ? new Date(Number(value)).toLocaleDateString() : '—';
}

// Three states, not two: an invited account is neither active nor disabled,
// and calling it either is wrong.
function stateOf(person) {
  if (person.invitation && person.invitation.status === 'PENDING') {
    return person.invitation.expired
      ? { tone: 'bad', label: tx("ui.staff.invite_expired_bbcffd2e") }
      : { tone: 'warn', label: tx("ui.staff.invited_53469df1") };
  }
  return person.isActive
    ? { tone: 'ok', label: tx("ui.staff.active_a733b809") }
    : { tone: 'flat', label: tx("ui.staff.deactivated_314a1adc") };
}

function WorkforceOverview({ staff, counts }) {
  const total = Math.max(1, counts.all);
  const activeShare = (counts.active / total) * 100;
  const invitedShare = (counts.invited / total) * 100;
  const inactiveShare = (counts.inactive / total) * 100;
  const roleCount = new Set(staff.map((person) => person.role.code)).size;
  const locationCount = new Set(staff.map((person) => person.location.id)).size;
  return (
    <section className="xo-workforce-overview" aria-label={tx('ui.staff.workforce_overview')}>
      <div className="xo-workforce-total">
        <span><Users aria-hidden="true" />{tx('ui.staff.workforce')}</span>
        <strong>{counts.all.toLocaleString()}</strong>
        <p>{tx('ui.staff.accounts_36bae316')}</p>
      </div>
      <div className="xo-workforce-composition">
        <div className="xo-workforce-track" aria-hidden="true">
          <span className="is-active" style={{ width: `${activeShare}%` }} />
          <span className="is-invited" style={{ width: `${invitedShare}%` }} />
          <span className="is-inactive" style={{ width: `${inactiveShare}%` }} />
        </div>
        <dl>
          <div className="is-active"><dt>{tx('ui.staff.active_a733b809')}</dt><dd>{counts.active.toLocaleString()}</dd></div>
          <div className="is-invited"><dt>{tx('ui.staff.awaiting_activation_0d3b7cdc')}</dt><dd>{counts.invited.toLocaleString()}</dd></div>
          <div className="is-inactive"><dt>{tx('ui.staff.deactivated_314a1adc')}</dt><dd>{counts.inactive.toLocaleString()}</dd></div>
        </dl>
      </div>
      <dl className="xo-workforce-reach">
        <div><dt>{tx('ui.staff.roles_in_use')}</dt><dd>{roleCount.toLocaleString()}</dd></div>
        <div><dt>{tx('ui.staff.locations_in_use')}</dt><dd>{locationCount.toLocaleString()}</dd></div>
      </dl>
    </section>
  );
}

function InviteForm({ locations, fixedLocation, onInvited, onCancel }) {
  const [form, setForm] = useState({
    email: '', firstName: '', lastName: '', role: '',
    locationId: fixedLocation ? String(fixedLocation.id) : '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const firstInput = useRef(null);

  const load = useCallback(() => loadAssignableRoles(), []);
  const roles = useLiveResource(['accounts'], load);

  useEffect(() => { firstInput.current?.focus(); }, []);

  useEffect(() => {
    const close = (event) => {
      if (event.key === 'Escape' && !busy) onCancel();
    };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [busy, onCancel]);

  function set(field, value) { setForm((c) => ({ ...c, [field]: value })); }

  async function submit(event) {
    event.preventDefault();
    setError('');
    setBusy(true);
    try {
      const result = await inviteStaff(form);
      // The account can exist while the email failed, and the difference
      // matters: one needs a resend, the other needs nothing.
      onInvited(result.emailSent
        ? tx('ui.staff.invitation_sent', { email: result.email })
        : tx('ui.staff.account_created_email_failed', { email: result.email }));
      setForm({
        email: '', firstName: '', lastName: '', role: '',
        locationId: fixedLocation ? String(fixedLocation.id) : '',
      });
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setBusy(false);
    }
  }

  return createPortal(
    <div id="xo" className="xo-staff-modal-backdrop" role="presentation"
      onMouseDown={(event) => {
        if (!busy && event.target === event.currentTarget) onCancel();
      }}>
      <section className="xo-staff-invite-modal" role="dialog" aria-modal="true"
        aria-labelledby="xo-staff-invite-title">
        <header>
          <span><UserPlus aria-hidden="true" /></span>
          <div>
            <h2 id="xo-staff-invite-title">{tx("ui.staff.invite_someone_9768ea31")}</h2>
            <p>{tx("ui.staff.they_get_a_link_that_works_once_and_expires_after_72_h_f0ae08dc")}</p>
          </div>
          <button type="button" aria-label={tx("ui.staff.cancel_77dfd213")}
            disabled={busy} onClick={onCancel}><X aria-hidden="true" /></button>
        </header>
        <form onSubmit={submit} className="xo-form xo-staff-invite-form">
          <div className="xo-staff-invite-body">
        <div className="xo-grid">
          <Field id="invite-first" label={tx("ui.staff.first_name_7e568a90")}>
            <input {...fieldAria('invite-first')} ref={firstInput} type="text"
              value={form.firstName} disabled={busy}
              onChange={(e) => set('firstName', e.target.value)} />
          </Field>
          <Field id="invite-last" label={tx("ui.staff.last_name_adec36a8")}>
            <input {...fieldAria('invite-last')} type="text" value={form.lastName} disabled={busy}
              onChange={(e) => set('lastName', e.target.value)} />
          </Field>
          <Field id="invite-email" label={tx("ui.staff.email_84add5b2")}>
            <input {...fieldAria('invite-email')} type="email" value={form.email} disabled={busy}
              onChange={(e) => set('email', e.target.value)} />
          </Field>
          {/* Only roles the server says this administrator may assign, so an
              ADMIN is never shown ADMIN_PLUS and then refused after typing. */}
          <Field id="invite-role" label={tx("ui.staff.role_c3f104d1")}>
            <XomicsSelect {...fieldAria('invite-role')} value={form.role} disabled={busy || roles.loading}
              onChange={(e) => set('role', e.target.value)}>
              <option value="">{tx("ui.staff.select_349ac8fb")}</option>
              {(roles.data || []).map((role) => (
                <option key={role.code} value={role.code}>{roleLabel(role.code)}</option>
              ))}
            </XomicsSelect>
          </Field>
          <Field id="invite-location" label={tx("ui.staff.location_d219c681")}>
            <XomicsSelect {...fieldAria('invite-location')} value={form.locationId} disabled={busy}
              onChange={(e) => set('locationId', e.target.value)}>
              {!fixedLocation && <option value="">{tx("ui.staff.select_349ac8fb")}</option>}
              {locations.map((location) => (
                <option key={location.id} value={location.id}>
                  {location.code} · {location.name} ({codeLabel('locationKind', location.kind)})
                </option>
              ))}
            </XomicsSelect>
          </Field>
        </div>

        {roles.loading && <p className="xo-muted">{tx('ui.staff.loading_roles')}</p>}
        {roles.error && <p className="xo-alert t-bad" role="alert">{roles.error}</p>}
        {error && <p className="xo-alert t-bad" role="alert">{error}</p>}
          </div>
          <footer>
            <button type="button" className="xo-btn-2" disabled={busy} onClick={onCancel}>
              {tx("ui.staff.cancel_77dfd213")}
            </button>
            <button type="submit" className="xo-btn"
              disabled={busy || roles.loading || !form.email || !form.firstName || !form.lastName
                || !form.role || !form.locationId}>
              <Mail aria-hidden="true" /> {busy ? tx("ui.staff.sending_cf765512") : tx("ui.staff.send_invitation_abb6cb2d")}
            </button>
          </footer>
        </form>
      </section>
    </div>,
    document.body,
  );
}

// The permission matrix. A separate card behind a separate permission,
// because changing what a role may do is a different act from managing one
// person, and one should not imply the other.
// Editing one person, opened from their row.
//
// Account editing stays attached to the selected directory row so role and
// location context remain visible while an administrator makes the change.
function EditPanel({
  person, roles, locations, languages, mayChangeLocation, onSaved, onCancel,
  actionBusy, onResend, onToggleActive, onDelete,
}) {
  const [form, setForm] = useState({
    firstName: person.firstName,
    lastName: person.lastName,
    phoneNumber: person.phoneNumber === null ? '' : person.phoneNumber,
    preferredLanguage: person.preferredLanguage || 'en',
    role: person.role.code,
    locationId: String(person.location.id),
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const set = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  // Only what actually changed. Sending the whole form would rewrite fields
  // nobody touched and log them as edited.
  function changes() {
    const out = {};
    if (form.firstName.trim() !== person.firstName) out.firstName = form.firstName.trim();
    if (form.lastName.trim() !== person.lastName) out.lastName = form.lastName.trim();
    if (form.role !== person.role.code) out.role = form.role;
    if (Number(form.locationId) !== person.location.id) out.locationId = Number(form.locationId);

    const phone = form.phoneNumber.trim();
    const currentPhone = person.phoneNumber === null ? '' : person.phoneNumber;
    if (phone !== currentPhone) out.phoneNumber = phone === '' ? null : phone;

    const language = form.preferredLanguage;
    const currentLanguage = person.preferredLanguage || 'en';
    if (language !== currentLanguage) out.preferredLanguage = language;

    return out;
  }

  const pending = changes();
  const changingRole = pending.role !== undefined;

  async function submit(event) {
    event.preventDefault();
    setError('');
    setSaving(true);
    try {
      await updateStaff(person.uuid, pending);
      onSaved(changingRole
        ? tx('ui.staff.role_changed', {
          name: `${person.firstName} ${person.lastName}`,
          role: roleLabel(form.role),
        })
        : tx('ui.staff.account_updated', { name: `${person.firstName} ${person.lastName}` }));
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setSaving(false);
    }
  }

  useEffect(() => {
    const close = (event) => {
      if (event.key === 'Escape' && !saving && !actionBusy) onCancel();
    };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [actionBusy, onCancel, saving]);

  return createPortal(
    <div id="xo" className="xo-staff-modal-backdrop" role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !saving && !actionBusy) onCancel();
      }}>
      <section className="xo-staff-edit-panel" role="dialog" aria-modal="true"
        aria-label={tx('ui.staff.manage_account', {
          name: `${person.firstName} ${person.lastName}`,
        })}>
      <header>
        <Who name={`${person.firstName} ${person.lastName}`} detail={person.email} />
        <button type="button" aria-label={tx("ui.staff.cancel_77dfd213")} onClick={onCancel}>
          <X aria-hidden="true" />
        </button>
      </header>
      <form onSubmit={submit} className="xo-staff-edit-form">
          <div className="xo-staff-edit-grid">
            <Field id={`edit-first-${person.uuid}`} label={tx("ui.staff.first_name_7e568a90")}>
              <input {...fieldAria(`edit-first-${person.uuid}`)} type="text" value={form.firstName}
                disabled={saving} onChange={(e) => set('firstName', e.target.value)} />
            </Field>
            <Field id={`edit-last-${person.uuid}`} label={tx("ui.staff.last_name_adec36a8")}>
              <input {...fieldAria(`edit-last-${person.uuid}`)} type="text" value={form.lastName}
                disabled={saving} onChange={(e) => set('lastName', e.target.value)} />
            </Field>
            <Field id={`edit-role-${person.uuid}`} label={tx("ui.staff.role_c3f104d1")}>
              <XomicsSelect {...fieldAria(`edit-role-${person.uuid}`)} value={form.role}
                disabled={saving} onChange={(e) => set('role', e.target.value)}>
                {roles.map((role) => (
                  <option key={role.code} value={role.code}>{roleLabel(role.code)}</option>
                ))}
              </XomicsSelect>
            </Field>
            <Field id={`edit-location-${person.uuid}`} label={tx("ui.staff.location_d219c681")}>
              <XomicsSelect {...fieldAria(`edit-location-${person.uuid}`)} value={form.locationId}
                disabled={saving || !mayChangeLocation} onChange={(e) => set('locationId', e.target.value)}>
                {locations.map((location) => (
                  <option key={location.id} value={location.id}>
                    {location.code} · {location.name} ({codeLabel('locationKind', location.kind)})
                  </option>
                ))}
              </XomicsSelect>
            </Field>
            <Field id={`edit-phone-${person.uuid}`} label={tx("ui.staff.phone_number_8961d3bf")}
              hint={tx("ui.staff.international_format_blank_removes_it_and_stops_sms_93aacd59")}>
              <input {...fieldAria(`edit-phone-${person.uuid}`, { hint: 'x' })} type="tel"
                value={form.phoneNumber} placeholder="+46701234567"
                disabled={saving} onChange={(e) => set('phoneNumber', e.target.value)} />
            </Field>
            <Field id={`edit-lang-${person.uuid}`} label={tx("ui.staff.preferred_language_1c7b0d77")}>
              <XomicsSelect {...fieldAria(`edit-lang-${person.uuid}`)} value={form.preferredLanguage}
                disabled={saving} onChange={(e) => set('preferredLanguage', e.target.value)}>
                {languages.map((languageOption) => (
                  <option key={languageOption.code} value={languageOption.code}>
                    {languageOption.nativeName || languageOption.name} · {languageOption.code.toUpperCase()}
                  </option>
                ))}
              </XomicsSelect>
            </Field>
          </div>

          {/* Not a warning: nothing is lost and nobody is interrupted. Their
              open session picks the change up over the socket. */}
          {changingRole && (
            <p className="xo-alert t-info">{tx("ui.staff.firstname_keeps_working_what_they_may_do_changes_immed_445e9ae3", { firstName: person.firstName })}</p>
          )}
          <p className="xo-muted">{tx("ui.staff.email_the_sign_in_address_cannot_be_changed_here_72fa02e2", { email: person.email })}</p>

          {error && <p className="xo-alert t-bad" role="alert">{error}</p>}

          <div className="xo-staff-edit-actions">
            <div className="xo-bar-group">
              {person.invitation?.status === 'PENDING' && (
                <button type="button" className="xo-link" disabled={actionBusy}
                  onClick={onResend}><Mail aria-hidden="true" />{tx("ui.staff.resend_0f4f4694")}</button>
              )}
              <button type="button" className="xo-link" disabled={actionBusy}
                onClick={onToggleActive}><KeyRound aria-hidden="true" />
                {person.isActive ? tx("ui.staff.deactivate_d65ded94") : tx("ui.staff.reactivate_cd28a172")}
              </button>
              <button type="button" className="xo-link t-bad" disabled={actionBusy}
                onClick={onDelete}><Trash2 aria-hidden="true" />{tx("ui.staff.delete_f6fdbe48")}</button>
            </div>
            <div className="xo-bar-group">
              <button type="button" className="xo-btn-2" disabled={saving} onClick={onCancel}>
                {tx("ui.staff.cancel_77dfd213")}
              </button>
              <button type="submit" className="xo-btn" disabled={saving || Object.keys(pending).length === 0}>
                {saving ? tx("ui.staff.saving_56a2285c") : tx("ui.staff.save_changes_179359b3")}
              </button>
            </div>
          </div>
      </form>
      </section>
    </div>,
    document.body,
  );
}

export default function XomicsStaff() {
  const { permissions, user } = useXomicsAuth();
  const { languages } = useXomicsI18n();
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState('');
  const [inviting, setInviting] = useState(false);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [viewMode, setViewMode] = useState('list');

  const load = useCallback(() => loadStaff(), []);
  const list = useLiveResource(['accounts'], load);
  // Loaded here rather than only inside the invite form: the edit row needs the
  // same list, and which roles this account may assign is the server's answer,
  // not something to derive from the roles already in use.
  const loadRoles = useCallback(() => loadAssignableRoles(), []);
  const roles = useLiveResource(['accounts'], loadRoles);
  const loadPlaces = useCallback(() => loadLocations(), []);
  const locations = useLiveResource(['locations'], loadPlaces);

  const mayManage = permissions.includes('STAFF_MANAGE');
  const mayManageElevated = permissions.includes('STAFF_MANAGE_ELEVATED');
  const [editing, setEditing] = useState(null);

  // ?account=<uuid> narrows the list to that person and, for someone who may
  // manage staff, opens them. Quick search links here. Read once, then dropped
  // from the address, so a later refresh of the list does not reopen them.
  const [searchParams, setSearchParams] = useSearchParams();
  const linkedAccount = searchParams.get('account');
  useEffect(() => {
    if (linkedAccount === null || list.data === null) return;
    const person = list.data.staff.find((candidate) => candidate.uuid === linkedAccount);
    if (person === undefined) {
      setError(tx('ui.search.linked_account_missing'));
    } else {
      setFilter('all'); setRoleFilter(''); setLocationFilter('');
      setQuery(person.email);
      if (mayManage) setEditing(person.uuid);
    }
    const next = new URLSearchParams(searchParams);
    next.delete('account');
    setSearchParams(next, { replace: true });
  }, [linkedAccount, list.data, mayManage, searchParams, setSearchParams]);
  const availableLocations = mayManageElevated
    ? (locations.data || [])
    : [user.location];

  const staff = useMemo(() => (list.data ? list.data.staff : []), [list.data]);

  const counts = useMemo(() => ({
    all: staff.length,
    active: staff.filter((p) => p.isActive && !(p.invitation?.status === 'PENDING')).length,
    invited: staff.filter((p) => p.invitation?.status === 'PENDING').length,
    inactive: staff.filter((p) => !p.isActive && p.invitation?.status !== 'PENDING').length,
  }), [staff]);

  const roleFilters = useMemo(() => [...new Set(staff.map((person) => person.role.code))]
    .sort((left, right) => roleLabel(left).localeCompare(roleLabel(right))), [staff]);
  const locationFilters = useMemo(() => {
    const unique = new Map(staff.map((person) => [person.location.id, person.location]));
    return [...unique.values()].sort((left, right) => left.name.localeCompare(right.name));
  }, [staff]);

  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return staff.filter((person) => {
      if (filter === 'active' && (!person.isActive || person.invitation?.status === 'PENDING')) return false;
      if (filter === 'invited' && person.invitation?.status !== 'PENDING') return false;
      if (filter === 'inactive'
        && (person.isActive || person.invitation?.status === 'PENDING')) return false;
      if (roleFilter && person.role.code !== roleFilter) return false;
      if (locationFilter && String(person.location.id) !== locationFilter) return false;
      if (!needle) return true;
      return `${person.firstName} ${person.lastName} ${person.email} ${roleLabel(person.role.code)} ${person.location.name} ${person.location.code}`
        .toLowerCase().includes(needle);
    });
  }, [filter, locationFilter, query, roleFilter, staff]);

  async function act(uuid, action, message) {
    setError(''); setNotice(''); setBusy(uuid);
    try {
      await action();
      if (message) setNotice(message);
      list.refresh();
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setBusy('');
    }
  }

  return (
    <div className="xo-staff-page">
      <Head
        title={tx("ui.staff.staff_a4730a22")}
        subtitle={list.data && !list.data.seesEveryone
          ? tx("ui.staff.accounts_with_elevated_permissions_are_not_shown_6eb005d7")
          : tx("ui.staff.everyone_with_an_account_841d4896")}
      >
        {mayManage && !inviting && (
          <button type="button" className="xo-btn" onClick={() => setInviting(true)}>
            <UserPlus aria-hidden="true" /> {tx("ui.staff.invite_someone_9768ea31")}
          </button>
        )}
      </Head>

      {error && <p className="xo-alert t-bad" role="alert">{error}</p>}
      {notice && <p className="xo-alert t-ok" role="status">{notice}</p>}

      {inviting && (
        <InviteForm
          locations={availableLocations}
          fixedLocation={mayManageElevated ? null : user.location}
          onCancel={() => setInviting(false)}
          onInvited={(message) => { setInviting(false); setNotice(message); list.refresh(); }}
        />
      )}

      {list.data && <WorkforceOverview staff={staff} counts={counts} />}

      <section className="xo-staff-directory">
        <header>
          <div>
            <h2>{tx("ui.staff.accounts_36bae316")}</h2>
            <p>{tx("ui.staff.showing_length_of_length2_032c3942", { length: shown.length, length2: staff.length })}</p>
          </div>
        </header>

        <div className="xo-staff-controls">
          <div className="xo-staff-toolbar">
            <SearchField
              className="xo-product-search"
              value={query}
              onChange={setQuery}
              placeholder={tx("ui.staff.search_name_email_or_role_3db280f2")}
              label={tx("ui.staff.search_staff_f5562ced")}
            />
            <div className="xo-staff-toolbar-actions">
              <XomicsSelect value={roleFilter} onChange={(event) => setRoleFilter(event.target.value)}>
                <option value="">{tx('ui.staff.all_roles')}</option>
                {roleFilters.map((role) => <option key={role} value={role}>{roleLabel(role)}</option>)}
              </XomicsSelect>
              <XomicsSelect value={locationFilter} onChange={(event) => setLocationFilter(event.target.value)}>
                <option value="">{tx('ui.staff.all_locations')}</option>
                {locationFilters.map((location) => (
                  <option key={location.id} value={location.id}>{location.code} · {location.name}</option>
                ))}
              </XomicsSelect>
              {(query || roleFilter || locationFilter) && (
                <button type="button" className="xo-link xo-staff-clear" onClick={() => {
                  setQuery(''); setRoleFilter(''); setLocationFilter('');
                }}><X aria-hidden="true" />{tx('ui.staff.clear_filters')}</button>
              )}
              <div className="xo-product-view-switch" role="group"
                aria-label={tx("ui.products.product_layout_2f81698d")}>
                <button type="button" className={viewMode === 'grid' ? 'is-active' : ''}
                  aria-pressed={viewMode === 'grid'} onClick={() => setViewMode('grid')}>
                  <LayoutGrid aria-hidden="true" />
                  <span>{tx("ui.products.grid_701c483f")}</span>
                </button>
                <button type="button" className={viewMode === 'list' ? 'is-active' : ''}
                  aria-pressed={viewMode === 'list'} onClick={() => setViewMode('list')}>
                  <List aria-hidden="true" />
                  <span>{tx("ui.products.list_a1fffaaa")}</span>
                </button>
              </div>
            </div>
          </div>
          <WorkspaceTabs
            value={filter}
            onChange={setFilter}
            label={tx("ui.staff.state_a7250206")}
            className="xo-staff-tabs"
            scrollable
            tabs={[
              { value: 'all', label: tx("ui.kits.all_6a720856"), count: counts.all, Icon: Users },
              { value: 'active', label: tx("ui.staff.active_a733b809"), count: counts.active, Icon: ShieldCheck },
              { value: 'invited', label: tx("ui.staff.invited_53469df1"), count: counts.invited, Icon: Mail },
              { value: 'inactive', label: tx("ui.staff.deactivated_314a1adc"), count: counts.inactive, Icon: KeyRound },
            ]}
          />
        </div>

        <div className="xo-staff-roster-wrap">
        {list.loading && !list.data && <Loading label={tx("ui.staff.reading_the_staff_list_5630eb79")} />}
        {list.error && <Failed message={list.error} onRetry={list.refresh} />}
        {list.data && shown.length === 0 && (
          <Empty
            title={query || filter !== 'all' || roleFilter || locationFilter
              ? tx("ui.staff.nothing_matches_17e7aebf") : tx("ui.staff.no_accounts_yet_5c5d5230")}
            detail={query || filter !== 'all' || roleFilter || locationFilter
              ? tx("ui.staff.try_a_different_search_or_filter_4e653834")
              : tx("ui.staff.invite_someone_to_get_started_7d581bba")}
          />
        )}
        {list.data && shown.length > 0 && viewMode === 'list' && (
          <div className={`xo-staff-roster${mayManage ? ' has-actions' : ''}`}>
            <div className="xo-staff-roster-head" aria-hidden="true">
              <span>{tx("ui.staff.person_8c41ae88")}</span>
              <span>{tx("ui.staff.role_c3f104d1")}</span>
              <span>{tx("ui.staff.location_d219c681")}</span>
              <span>{tx("ui.staff.state_a7250206")}</span>
              <span>{tx("ui.staff.joined_43a1c626")}</span>
              {mayManage && <span />}
            </div>
            <div className="xo-staff-roster-body">
              {shown.map((person) => {
                const state = stateOf(person);
                return (
                    <article key={person.uuid} className={editing === person.uuid ? 'is-open' : ''}>
                      <Who name={`${person.firstName} ${person.lastName}`} detail={person.email} />
                      <span className="xo-staff-role"><ShieldCheck aria-hidden="true" />{roleLabel(person.role.code)}</span>
                      <span className="xo-staff-location"><MapPin aria-hidden="true" />
                        <span><strong>{person.location.code}</strong><small>{person.location.name}</small></span>
                      </span>
                      <span className="xo-staff-state"><Pill tone={state.tone}>{state.label}</Pill></span>
                      <time dateTime={person.createdAt
                        ? new Date(Number(person.createdAt)).toISOString() : undefined}>{when(person.createdAt)}</time>
                      {mayManage && (
                        <button type="button" className="xo-staff-manage" disabled={busy === person.uuid}
                          aria-haspopup="dialog"
                          onClick={() => {
                            setNotice(''); setError('');
                            setEditing(person.uuid);
                          }}>
                          <Pencil aria-hidden="true" />{tx('ui.staff.manage')}
                        </button>
                      )}
                    </article>
                );
              })}
            </div>
          </div>
        )}
        {list.data && shown.length > 0 && viewMode === 'grid' && (
          <div className="xo-staff-card-grid">
            {shown.map((person) => {
              const state = stateOf(person);
              return (
                <article className="xo-staff-card" key={person.uuid}>
                  <header>
                    <Who name={`${person.firstName} ${person.lastName}`} detail={person.email} />
                    <Pill tone={state.tone}>{state.label}</Pill>
                  </header>
                  <dl>
                    <div><dt>{tx("ui.staff.role_c3f104d1")}</dt><dd><ShieldCheck aria-hidden="true" />{roleLabel(person.role.code)}</dd></div>
                    <div><dt>{tx("ui.staff.location_d219c681")}</dt><dd><MapPin aria-hidden="true" />{person.location.code} · {person.location.name}</dd></div>
                    <div><dt>{tx("ui.staff.joined_43a1c626")}</dt><dd>{when(person.createdAt)}</dd></div>
                  </dl>
                  {mayManage && (
                    <footer>
                      <button type="button" className="xo-staff-manage" disabled={busy === person.uuid}
                        aria-haspopup="dialog" onClick={() => { setNotice(''); setError(''); setEditing(person.uuid); }}>
                        <Pencil aria-hidden="true" />{tx('ui.staff.manage')}
                      </button>
                    </footer>
                  )}
                </article>
              );
            })}
          </div>
        )}
        </div>
        <footer>
          {tx("ui.staff.showing_length_of_length2_032c3942", { length: shown.length, length2: staff.length })}
        </footer>
      </section>

      {editing && (() => {
        const person = staff.find((candidate) => candidate.uuid === editing);
        return person ? (
          <EditPanel
            person={person}
            roles={roles.data === null ? [] : roles.data}
            locations={availableLocations}
            mayChangeLocation={mayManageElevated}
            languages={languages}
            actionBusy={busy === person.uuid}
            onCancel={() => setEditing(null)}
            onSaved={(message) => { setEditing(null); setNotice(message); list.refresh(); }}
            onResend={() => act(person.uuid, () => resendStaffInvitation(person.uuid),
              tx("ui.staff.a_fresh_invitation_is_on_its_way_to_email_1ea1fd9f", { email: person.email }))}
            onToggleActive={() => act(person.uuid,
              () => updateStaff(person.uuid, { isActive: !person.isActive }),
              tx('ui.staff.account_state_changed', { name: `${person.firstName} ${person.lastName}` }))}
            onDelete={() => {
              if (window.confirm(tx('ui.staff.confirm_delete', { email: person.email }))) {
                act(person.uuid, () => deleteStaff(person.uuid),
                  tx("ui.staff.email_was_deleted_7bded1d9", { email: person.email }));
              }
            }}
          />
        ) : null;
      })()}

    </div>
  );
}
